import { ArdoqId } from './coreTypes';

// Add new agent types to this union as they are introduced
export type AgentType = 'report_insight_with_asset_context' | 'product_tour';

export const DEFAULT_ASSISTANT_AGENT_NAME = 'ardoq_assistant';

// Assistant variants selectable via the internal assistant-config toolkit.
export type AssistantAgentName =
  | typeof DEFAULT_ASSISTANT_AGENT_NAME
  | 'ardoq_assistant_graphlake'
  | 'unrestricted_code_agent';

export type AssistantModel =
  | 'anthropic-claude-sonnet-5-5'
  | 'anthropic-claude-opus-4-6'
  | 'anthropic-claude-opus-5-5'
  | 'qwen3';

export type AssistantConfig = {
  agent: AssistantAgentName;
  model: AssistantModel;
};

export const DEFAULT_ASSISTANT_CONFIG: AssistantConfig = {
  agent: DEFAULT_ASSISTANT_AGENT_NAME,
  model: 'anthropic-claude-sonnet-5-5',
};

export type DocumentResourceType = 'org' | 'user';

export type DocumentReference = {
  id: ArdoqId;
  name: string;
  type: string;
  resourceType: DocumentResourceType;
};

export type ApiDocumentReference = Pick<
  DocumentReference,
  'id' | 'resourceType'
>;

export type UserChatMessage = {
  role: 'user';
  content: string;
  reasoning?: string;
};

export type AssistantChatMessage = {
  role: 'assistant';
  content: string;
  reasoning?: string;
};

export type DocumentChatMessage = {
  role: 'document';
  content: DocumentReference;
};

export enum ToolCallActionCategory {
  PLAN = 'plan',
  SEARCH = 'search',
  LIST = 'list',
  ANALYZE = 'analyze',
  EXECUTE = 'execute',
  FILTER = 'filter',
  SUGGEST_VALUES = 'suggest_values',
  AGGREGATE = 'aggregate',
  RESOLVE = 'resolve',
  TRAVERSE = 'traverse',
  FETCH_EXTERNAL = 'fetch_external',
}

export type ToolCallMessage = {
  role: 'tool_call';
  actionDescription: string;
  actionCategory: ToolCallActionCategory;
};

export type APIChatMessage =
  | UserChatMessage
  | AssistantChatMessage
  | DocumentChatMessage;

/** A durable pause asking the active view to run one UI command.
 * Backend fields that are not the frontend's concern (chatSessionId, outerToolCallId,
 * source job, trace) are stripped by ardoq-api before this reaches the client. */
export type UiCommandInteractionRequest = {
  kind: 'ui_command';
  commandName: string;
  payload: unknown;
};

/** A durable pause asking the user a free-text question. */
export type UserInputInteractionRequest = {
  kind: 'user_input';
  prompt: string;
};

export type UserInlineOption = {
  id: string;
  label: string;
};

export type UserInlineOptionsInteractionRequest = {
  kind: 'user_inline_options';
  prompt: string;
  options: UserInlineOption[];
};

export type UserDropdownOption = {
  id: string;
  label: string;
};

export type UserDropdownInteractionRequest = {
  kind: 'user_dropdown';
  prompt: string;
  options: UserDropdownOption[];
};

export type InteractionRequest =
  | UiCommandInteractionRequest
  | UserInputInteractionRequest
  | UserInlineOptionsInteractionRequest
  | UserDropdownInteractionRequest;

/** The outcome the frontend reports back after acting on a UI command: the command
 * ran (optionally returning a value the specialist sees), or it was declined with a
 * precise reason the specialist can act on. */
export type UiCommandOutcome =
  | { status: 'applied'; result?: unknown }
  | { status: 'rejected'; reason: string };

export type UiCommandInteractionResult = {
  kind: 'ui_command';
  outcome: UiCommandOutcome;
};

export type UserInteractionResult = {
  kind: 'user_input';
  text: string;
};

export type UserInlineOptionsInteractionResult = {
  kind: 'user_inline_options';
  selectedOptionId: string;
};

export type UserDropdownInteractionResult = {
  kind: 'user_dropdown';
  selectedOptionId: string;
};

/** The authoritative interaction result submitted to ardoq-api. */
export type AgentInteractionResult =
  | UiCommandInteractionResult
  | UserInteractionResult
  | UserInlineOptionsInteractionResult
  | UserDropdownInteractionResult;

export type InteractionStatus = 'pending' | 'resolved' | 'cancelled';

export type UserInputInteractionContent = {
  kind: 'user_input';
  interactionId: string;
  status: InteractionStatus;
  request: UserInputInteractionRequest;
  result?: UserInteractionResult;
};

export type UserInlineOptionsInteractionContent = {
  kind: 'user_inline_options';
  interactionId: string;
  status: InteractionStatus;
  request: UserInlineOptionsInteractionRequest;
  result?: UserInlineOptionsInteractionResult;
};

export type UserDropdownInteractionContent = {
  kind: 'user_dropdown';
  interactionId: string;
  status: InteractionStatus;
  request: UserDropdownInteractionRequest;
  result?: UserDropdownInteractionResult;
};

export type UiCommandInteractionContent = {
  kind: 'ui_command';
  interactionId: string;
  status: InteractionStatus;
  request: UiCommandInteractionRequest;
  result?: UiCommandInteractionResult;
};

export type InteractionContent =
  | UserInputInteractionContent
  | UserInlineOptionsInteractionContent
  | UserDropdownInteractionContent
  | UiCommandInteractionContent;

export type APIInteractionMessage = {
  role: 'interaction';
  content: InteractionContent;
};

export type APIChatHistoryMessage =
  | (APIChatMessage & { timestamp: string; exchangeId: string })
  | (ToolCallMessage & { timestamp: string; exchangeId: string })
  | (APIInteractionMessage & { timestamp: string; exchangeId: string });

export type HistoricChat = {
  _id: string;
  orgId: string;
  title: string;
  created: string;
  pinned: boolean;
  webSearchEnabled: boolean;
  inProgressExchangeId: string | null;
  hasUnreadResponse: boolean;
};

export type ChatCancellationResponse = {
  cancelled: boolean;
};

export type LoadedChat = {
  _id: string;
  messages: APIChatHistoryMessage[];
  renderMetadata: Record<string, unknown>;
  webSearchEnabled: boolean;
  inProgressExchangeId: string | null;
};

export type ChatSessionExport = {
  _id: string;
  title: string;
  created: string;
  exportedAt: string;
  // Raw debug dump; message shapes change as roles and database columns are added.
  messages: Record<string, unknown>[];
};
